import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from './buttons';
import { NetworkBanner } from './states';
import { BackLink } from './process';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Focus trap + Escape + scroll lock for modal layers; focus returns to the opener on close. */
export function useModalLayer(open: boolean, ref: RefObject<HTMLElement | null>, onClose: () => void, dismissible = true) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const panel = ref.current;
    (panel?.querySelector<HTMLElement>('[data-autofocus]') ?? panel)?.focus();
    document.body.classList.add('scroll-locked');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation();
        close.current();
      }
      if (e.key === 'Tab' && panel) {
        const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.classList.remove('scroll-locked');
      prev?.focus();
    };
  }, [open, dismissible, ref]);
}

/**
 * Sheet sliding in from the right over the page content (docs/10 "Sheet 300 ms out", design/BelegUpload).
 * The sidebar stays visible on wide screens; on phones the sheet covers the full width.
 */
export function Sheet({ open, onClose, label, children, dismissible = true }: { open: boolean; onClose: () => void; label: string; children: ReactNode; dismissible?: boolean }) {
  const { t } = useTranslation('common');
  const ref = useRef<HTMLDivElement>(null);
  useModalLayer(open, ref, onClose, dismissible);
  if (!open) return null;
  return (
    <div className="sheet-layer" onMouseDown={(e) => dismissible && e.target === e.currentTarget && onClose()}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={label} ref={ref} tabIndex={-1}>
        {dismissible && <IconButton className="sheet-close" icon="close" label={t('close')} onClick={onClose} />}
        {children}
      </div>
    </div>
  );
}

/**
 * Work screen without sidebar (design/BelegDetail, design/AbrechnungVorschau): white top bar with back link, title,
 * status badge and actions; the page below gets the full width. Used for review and document views.
 */
export function FocusLayout({ back, title, badge, meta, actions, children, after, bodyClassName = '' }: { back: { to: string; label: string }; title: ReactNode; badge?: ReactNode; meta?: ReactNode; actions?: ReactNode; children: ReactNode; after?: ReactNode; bodyClassName?: string }) {
  const { t } = useTranslation('common');
  const titleId = useId();
  return (
    <div className="focus-shell">
      <a className="skip-link" href="#focus-main">{t('ui.skipToContent')}</a>
      <header className="focus-bar" aria-labelledby={titleId}>
        <div className="fb-start">
          <BackLink to={back.to}>{back.label}</BackLink>
          <h1 id={titleId} className="fb-title">{title}</h1>
          {badge}
        </div>
        {(meta || actions) && (
          <div className="fb-end">
            {meta && <span className="fb-meta">{meta}</span>}
            {actions}
          </div>
        )}
      </header>
      <main id="focus-main" className={`focus-main ${bodyClassName}`} tabIndex={-1}>
        <NetworkBanner />
        {children}
      </main>
      {after}
    </div>
  );
}
